import React from 'react';
import { Dialog, DialogActions, DialogContent, DialogTitle, Button, IconButton, Typography, Box, TextField, Stepper, Step, StepLabel, styled, StepConnector, InputAdornment, BaseTextFieldProps, Select, MenuItem, OutlinedInput, CircularProgress, Avatar } from '@mui/material';
import { EditWithUnderscroreIcon, ModalClose } from '../../assets/icons';
import Grid from '@mui/material/Grid2';
import { FormControl } from "@mui/material";
import AuthService from '../../services/auth.service';
import { SignupRequest } from '../../types/auth';
import moment from 'moment-timezone';
import { CountryIso2, defaultCountries, FlagImage, parseCountry, usePhoneInput } from 'react-international-phone';
import 'react-international-phone/style.css';
import { Roles } from '../../types/enums';
import { decodeMetadataToken } from '../../utils/token.utils';
import { toast } from 'react-toastify';
import { validateUserPermissions, validateEmail, validateAndSanitizeName, sanitizeTextInput } from '../../utils/common.util';

interface AddUserProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
}

interface UserFormData {
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  password: string;
  confirmPassword: string;
  timezone: string;
  easywebinarRole: Roles;
  easycrmeRole: Roles;
}

interface MUIPhoneProps extends BaseTextFieldProps {
  value: string;
  onChange: (phone: string) => void;
}

const MuiPhone: React.FC<MUIPhoneProps> = ({
  value,
  onChange,
  ...restProps
}) => {
  const { inputValue, handlePhoneValueChange, inputRef, country, setCountry } =
    usePhoneInput({
      defaultCountry: 'us',
      value,
      countries: defaultCountries,
      onChange: (data) => {
        onChange(data.phone);
      },
      disableCountryGuess: true,
      forceDialCode: true,
    });

  return (
    <TextField
      variant="outlined"
      value={inputValue}
      onChange={handlePhoneValueChange}
      type="tel"
      inputRef={inputRef}
      {...restProps}
      sx={{
        "& .MuiOutlinedInput-root": {
          borderRadius: '8px',
          borderColor: '#C7C7C7',
        },
        "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
          borderColor: '#333333',
          border: '1px solid'
        },
        "& .MuiOutlinedInput-input": {
          padding: '14px 16px',
          paddingLeft: '0',
        }
      }}
      slotProps={{
        input: {
          startAdornment: <InputAdornment
            position="start"
            style={{ marginRight: '2px', marginLeft: '-8px' }}
          >
            <Select
              MenuProps={{
                style: {
                  height: '300px',
                  width: '360px',
                  top: '10px',
                  left: '-34px',
                },
              }}
              sx={{
                width: 'max-content',
                fieldset: { display: 'none' },
                '.MuiSelect-select': {
                  padding: '8px',
                  paddingRight: '24px !important',
                },
              }}
              value={country.iso2}
              onChange={(e) => setCountry(e.target.value as CountryIso2)}
              renderValue={(value) => (
                <FlagImage iso2={value} style={{ display: 'flex' }} />
              )}
            >
              {defaultCountries.map((c) => {
                const country = parseCountry(c);
                return (
                  <MenuItem key={country.iso2} value={country.iso2}>
                    <FlagImage iso2={country.iso2} style={{ marginRight: '8px' }} />
                    <Typography marginRight="8px">{country.name}</Typography>
                    <Typography color="gray">+{country.dialCode}</Typography>
                  </MenuItem>
                );
              })}
            </Select>
          </InputAdornment>
        },
      }}
    />
  );
};

const steps = ['Details', 'Set Roles', 'Finished'];

interface Step1Props {
  formData: UserFormData;
  setFormData: (data: UserFormData) => void;
  errors: Record<string, string>;
  setErrors: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  profilePhoto: File | null;
  profilePhotoUrl: string;
  handleFileSelect: (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleUploadClick: () => void;
  fileInputRef: React.RefObject<HTMLInputElement>;
  showPassword: boolean;
  showConfirmPassword: boolean;
  handleClickShowPassword: () => void;
  handleClickShowConfirmPassword: () => void;
  handleMouseDownPassword: (event: React.MouseEvent<HTMLButtonElement>) => void;
}

const Step1: React.FC<Step1Props> = ({ 
  formData, 
  setFormData, 
  errors, 
  setErrors,
  profilePhoto,
  profilePhotoUrl,
  handleFileSelect,
  handleUploadClick,
  fileInputRef,
  showPassword,
  showConfirmPassword,
  handleClickShowPassword,
  handleClickShowConfirmPassword,
  handleMouseDownPassword
}) => {
  const [touched, setTouched] = React.useState<Record<string, boolean>>({});
  const [companyName, setCompanyName] = React.useState<string>('');

  // Fetch company name from metadata token on component mount with security validation
  React.useEffect(() => {
    try {
      // Security: Validate user permissions before allowing access to company data
      const permissionCheck = validateUserPermissions('OWNER');
      if (!permissionCheck.isValid) {
        toast.error(permissionCheck.error || 'Access denied: Insufficient permissions');
        return;
      }
      
      const metadata = decodeMetadataToken();
      if (metadata?.companies && metadata.companies.length > 0) {
        setCompanyName(metadata.companies[0].name || '');
      }
    } catch (error) {
      console.error('Error validating user permissions:', error);
      toast.error('Error validating permissions');
    }
  }, []);

  const validatePhone = (phoneNumber: string) => {
    const digitsOnly = phoneNumber.replace(/\D/g, '');
    return digitsOnly.length >= 10 && digitsOnly.length <= 15;
  };

  const validatePassword = (password: string) => {
    const minLength = 8;
    const hasNumber = /\d/.test(password);
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);
    return password.length >= minLength && hasNumber && hasSpecialChar;
  };

  const handleChange = (field: keyof UserFormData) => (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setFormData({ ...formData, [field]: value });

    // Validate first name with hyperlink injection protection
    if (field === 'firstName') {
      if (!value.trim()) {
        setErrors(prev => ({ ...prev, [field]: 'First name is required' }));
      } else {
        const nameValidation = validateAndSanitizeName(value, 'First name');
        if (!nameValidation.isValid) {
          setErrors(prev => ({ ...prev, [field]: nameValidation.error ?? 'Invalid name' }));
        } else {
          setErrors(prev => ({ ...prev, [field]: '' }));
        }
      }
    }

    // Validate email with enhanced security validation
    if (field === 'email') {
      if (!value) {
        setErrors(prev => ({ ...prev, email: 'Email is required' }));
      } else {
        const emailValidation = validateEmail(value);
        if (!emailValidation.isValid) {
          setErrors(prev => ({ ...prev, email: emailValidation.error || 'Please enter a valid email address' }));
        } else {
          setErrors(prev => ({ ...prev, email: '' }));
        }
      }
    }

    // Validate password
    if (field === 'password') {
      if (!value) {
        setErrors(prev => ({ ...prev, password: 'Password is required' }));
      } else if (!validatePassword(value)) {
        setErrors(prev => ({ 
          ...prev, 
          password: 'Password must be at least 8 characters long and contain at least one number and one special character'
        }));
      } else {
        setErrors(prev => ({ ...prev, password: '' }));
      }

      // Also validate confirm password when password changes
      if (formData.confirmPassword && value !== formData.confirmPassword) {
        setErrors(prev => ({ ...prev, confirmPassword: 'Passwords do not match' }));
      } else {
        setErrors(prev => ({ ...prev, confirmPassword: '' }));
      }
    }

    // Validate confirm password
    if (field === 'confirmPassword') {
      if (!value) {
        setErrors(prev => ({ ...prev, confirmPassword: 'Please confirm your password' }));
      } else if (value !== formData.password) {
        setErrors(prev => ({ ...prev, confirmPassword: 'Passwords do not match' }));
      } else {
        setErrors(prev => ({ ...prev, confirmPassword: '' }));
      }
    }
  };

  const handlePhoneChange = (phone: string) => {
    setFormData({ ...formData, phoneNumber: phone });
    setTouched(prev => ({ ...prev, phoneNumber: true }));
    
    if (touched.phoneNumber) {
      if (!phone) {
        setErrors(prev => ({ ...prev, phoneNumber: 'Phone number is required' }));
      } else if (!validatePhone(phone)) {
        setErrors(prev => ({ ...prev, phoneNumber: 'Please enter a valid phone number' }));
      } else {
        setErrors(prev => ({ ...prev, phoneNumber: '' }));
      }
    }
  };

  return (
    <Grid container rowSpacing={{ xs: 3, sm: 3, md: 3 }} columnSpacing={{ xs: 3, sm: 2, md: 3 }}>
      <Grid size={{ xs: 12, sm: 6, md: 6, lg: 6 }} display="flex" justifyContent={{ xs: "center" }}>
        <Box position="relative" borderRadius="100%" width="102px" height="102px">
          {/* Hidden file input */}
          <input
            type="file"
            ref={fileInputRef}
            style={{ display: 'none' }}
            accept="image/*"
            onChange={handleFileSelect}
          />
          
          {/* Profile photo container */}
          <Box 
            position="relative"
            width="100%"
            height="100%"
            borderRadius="50%"
            overflow="hidden"
            onClick={handleUploadClick}
            sx={{ 
              cursor: "pointer",
              '&:hover .edit-overlay': {
                opacity: 1
              }
            }}
          >
            {profilePhotoUrl ? (
              // Show selected image
              <>
                <img 
                  src={profilePhotoUrl} 
                  alt="Selected profile" 
                  style={{ 
                    width: '100%', 
                    height: '100%', 
                    objectFit: 'cover',
                    display: 'block'
                  }} 
                />
                {/* Edit overlay that appears on hover */}
                <Box 
                  className="edit-overlay"
                  position="absolute"
                  top={0}
                  left={0}
                  width="100%"
                  height="100%"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  sx={{ 
                    backgroundColor: "rgba(0, 0, 0, 0.5)", 
                    opacity: 0,
                    transition: "opacity 0.2s ease-in-out"
                  }}
                >
                  <EditWithUnderscroreIcon />
                </Box>
              </>
            ) : (
              // Show default avatar
              <Avatar 
                sx={{ 
                  width: '100%', 
                  height: '100%',
                  bgcolor: '#E6E6E6'
                }}
              />
            )}
            
            {/* Upload overlay - only show when no photo is selected */}
            {!profilePhotoUrl && (
              <Box 
                position="absolute"
                top={0}
                left={0}
                width="100%"
                height="100%"
                display="flex"
                alignItems="center"
                justifyContent="center"
                sx={{ 
                  border: "1px dashed #A3A3A3", 
                  backgroundColor: "rgba(230, 230, 230, 0.7)", 
                  cursor: "pointer"
                }}
              >
                <Box textAlign="center">
                  <img src="https://easywebinar-site.easywebinar.com/images/autheticator-app-images/camera-add.svg" alt="camera_add_icon" />
                  <Typography variant="caption" color="#666666" display="block" fontWeight="500">Upload Photo</Typography>
                </Box>
              </Box>
            )}
          </Box>
        </Box>
      </Grid>
      <Grid size={{ xs: 12, sm: 6, md: 6, lg: 6 }}>
        <Typography color='#333333' fontSize='14px' fontWeight='500' mb='12px' sx={{ opacity: '0.6' }}>Company Name</Typography>
        <TextField
          value={companyName}
          variant="outlined"
          placeholder='Company name'
          disabled
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: '8px',
              borderColor: '#C7C7C7',
            },
            "& .MuiOutlinedInput-root.Mui-disabled": {
              backgroundColor: "#f5f5f5",
            },
            "& .MuiOutlinedInput-input.Mui-disabled": {
              color: "#666666",
              WebkitTextFillColor: "#666666",
              opacity: 0.8,
            },
            "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
              borderColor: '#333333',
              border: '1px solid'
            },
            "& .MuiOutlinedInput-input": {
              padding: '14px 16px',
            },
            "&.MuiInputBase-input::placeholder": {
              color: '#333333',
              fontSize: '16px',
              opacity: '0.8'
            },
          }}
          fullWidth
        />
      </Grid>
      <Grid size={{ xs: 12, sm: 6, md: 6, lg: 6 }}>
        <Typography color='#333333' fontSize='14px' fontWeight='500' mb='12px' sx={{ opacity: '0.6' }}>First Name*</Typography>
        <TextField
          value={formData.firstName}
          onChange={handleChange('firstName')}
          error={!!errors.firstName}
          helperText={errors.firstName}
          variant="outlined"
          placeholder='Enter first name'
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: '8px',
              borderColor: '#C7C7C7',
            },

            "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
              borderColor: '#333333',
              border: '1px solid'
            },

            "& .MuiOutlinedInput-input": {
              padding: '14px 16px',
            },
            "&.MuiInputBase-input::placeholder": {
              color: '#333333',
              fontSize: '16px',
              opacity: '0.8'
            },
          }}
          fullWidth
        />
      </Grid>
      <Grid size={{ xs: 12, sm: 6, md: 6, lg: 6 }}>
        <Typography color='#333333' fontSize='14px' fontWeight='500' mb='12px' sx={{ opacity: '0.6' }}>Last Name(Optional)</Typography>
        <TextField
          value={formData.lastName}
          onChange={handleChange('lastName')}
          variant="outlined"
          placeholder='Enter last name'
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: '8px',
              borderColor: '#C7C7C7',
            },

            "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
              borderColor: '#333333',
              border: '1px solid'
            },

            "& .MuiOutlinedInput-input": {
              padding: '14px 16px',
            },
            "&.MuiInputBase-input::placeholder": {
              color: '#333333',
              fontSize: '16px',
              opacity: '0.8'
            },
          }}
          fullWidth
        />
      </Grid>
      <Grid size={{ xs: 12, sm: 6, md: 6, lg: 6 }}>
        <Typography color='#333333' fontSize='14px' fontWeight='500' mb='12px' sx={{ opacity: '0.6' }}>Email*</Typography>
        <TextField
          value={formData.email}
          onChange={handleChange('email')}
          error={!!errors.email}
          helperText={errors.email}
          variant="outlined"
          placeholder='Enter email'
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: '8px',
              borderColor: '#C7C7C7',
            },

            "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
              borderColor: '#333333',
              border: '1px solid'
            },

            "& .MuiOutlinedInput-input": {
              padding: '14px 16px',
            },
            "&.MuiInputBase-input::placeholder": {
              color: '#333333',
              fontSize: '16px',
              opacity: '0.8'
            },
          }}
          fullWidth
        />
      </Grid>
      <Grid size={{ xs: 12, sm: 6, md: 6, lg: 6 }}>
        <Typography color='#333333' fontSize='14px' fontWeight='500' mb='12px' sx={{ opacity: '0.6' }}>Phone Number*</Typography>
        <MuiPhone
          value={formData.phoneNumber}
          onChange={handlePhoneChange}
          onBlur={() => {
            setTouched(prev => ({ ...prev, phoneNumber: true }));
            if (!formData.phoneNumber) {
              setErrors(prev => ({ ...prev, phoneNumber: 'Phone number is required' }));
            }
          }}
          error={touched.phoneNumber && !!errors.phoneNumber}
          helperText={touched.phoneNumber ? errors.phoneNumber : ''}
          fullWidth
        />
      </Grid>
      <Grid size={{ xs: 12, sm: 6, md: 6, lg: 6 }}>
        <Typography color='#333333' fontSize='14px' fontWeight='500' mb='12px' sx={{ opacity: '0.6' }}>Password*</Typography>
        <TextField
          value={formData.password}
          onChange={handleChange('password')}
          error={!!errors.password}
          helperText={errors.password}
          variant="outlined"
          placeholder='Enter password'
          type={showPassword ? 'text' : 'password'}
          sx={{
            width: '100%',
            "& .MuiOutlinedInput-root": {
              borderRadius: '8px',
              borderColor: '#C7C7C7',
              background: '#ffffff',
              paddingRight: '0'
            },

            "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
              borderColor: '#333333',
              border: '1px solid'
            },

            "& .MuiOutlinedInput-input": {
              padding: '14px 16px',
              background: '#ffffff',
              borderRadius: '8px'
            },
            "&.MuiInputBase-input::placeholder": {
              color: '#333333',
              fontSize: '16px',
              opacity: '0.8'
            },
            // Add these styles to remove autofill color
            "& input:-webkit-autofill": {
              WebkitBoxShadow: "0 0 0 100px #ffffff inset",
              WebkitTextFillColor: "#333333",
            },
            "& input:-webkit-autofill:focus": {
              WebkitBoxShadow: "0 0 0 100px #ffffff inset",
              WebkitTextFillColor: "#333333",
            }
          }}
          slotProps={{
            input: {
              endAdornment: <InputAdornment position="start"><IconButton onClick={handleClickShowPassword}
                onMouseDown={() => handleMouseDownPassword}
                onMouseUp={() => handleMouseDownPassword}  >
                {showPassword ? <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M15.5799 11.9999C15.5799 13.9799 13.9799 15.5799 11.9999 15.5799C10.0199 15.5799 8.41992 13.9799 8.41992 11.9999C8.41992 10.0199 10.0199 8.41992 11.9999 8.41992C13.9799 8.41992 15.5799 10.0199 15.5799 11.9999Z" stroke="#333333" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  <path d="M12.0001 20.27C15.5301 20.27 18.8201 18.19 21.1101 14.59C22.0101 13.18 22.0101 10.81 21.1101 9.39997C18.8201 5.79997 15.5301 3.71997 12.0001 3.71997C8.47009 3.71997 5.18009 5.79997 2.89009 9.39997C1.99009 10.81 1.99009 13.18 2.89009 14.59C5.18009 18.19 8.47009 20.27 12.0001 20.27Z" stroke="#333333" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                  : <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M14.5299 9.47004L9.46992 14.53C8.81992 13.88 8.41992 12.99 8.41992 12C8.41992 10.02 10.0199 8.42004 11.9999 8.42004C12.9899 8.42004 13.8799 8.82004 14.5299 9.47004Z" stroke="#BABABA" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M17.8201 5.76998C16.0701 4.44998 14.0701 3.72998 12.0001 3.72998C8.47009 3.72998 5.18009 5.80998 2.89009 9.40998C1.99009 10.82 1.99009 13.19 2.89009 14.6C3.68009 15.84 4.60009 16.91 5.60009 17.77" stroke="#BABABA" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M8.41992 19.5301C9.55992 20.0101 10.7699 20.2701 11.9999 20.2701C15.5299 20.2701 18.8199 18.1901 21.1099 14.5901C22.0099 13.1801 22.0099 10.8101 21.1099 9.40005C20.7799 8.88005 20.4199 8.39005 20.0499 7.93005" stroke="#BABABA" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M15.5099 12.7C15.2499 14.11 14.0999 15.26 12.6899 15.52" stroke="#BABABA" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M9.47 14.53L2 22" stroke="#BABABA" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M22 2L14.53 9.47" stroke="#BABABA" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                }
              </IconButton>
              </InputAdornment>,
              startAdornment: <InputAdornment position="start"><Box display={"flex"}
                onMouseDown={() => handleMouseDownPassword}
                onMouseUp={() => handleMouseDownPassword}  >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M6 10V8C6 4.69 7 2 12 2C17 2 18 4.69 18 8V10" stroke="#333333" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  <path d="M12 18.5C13.3807 18.5 14.5 17.3807 14.5 16C14.5 14.6193 13.3807 13.5 12 13.5C10.6193 13.5 9.5 14.6193 9.5 16C9.5 17.3807 10.6193 18.5 12 18.5Z" stroke="#333333" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  <path d="M17 22H7C3 22 2 21 2 17V15C2 11 3 10 7 10H17C21 10 22 11 22 15V17C22 21 21 22 17 22Z" stroke="#333333" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </Box>
              </InputAdornment>
            },
          }}
        />
      </Grid>
      <Grid size={{ xs: 12, sm: 6, md: 6, lg: 6 }}>
        <Typography color='#333333' fontSize='14px' fontWeight='500' mb='12px' sx={{ opacity: '0.6' }}>Confirm Password*</Typography>
        <TextField
          value={formData.confirmPassword}
          onChange={handleChange('confirmPassword')}
          error={!!errors.confirmPassword}
          helperText={errors.confirmPassword}
          variant="outlined"
          placeholder='Confirm password'
          type={showConfirmPassword ? 'text' : 'password'}
          sx={{
            width: '100%',
            "& .MuiOutlinedInput-root": {
              borderRadius: '8px',
              borderColor: '#C7C7C7',
              background: '#ffffff',
              paddingRight: '0'
            },

            "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
              borderColor: '#333333',
              border: '1px solid'
            },

            "& .MuiOutlinedInput-input": {
              padding: '14px 16px',
              background: '#ffffff',
              borderRadius: '8px'
            },
            "&.MuiInputBase-input::placeholder": {
              color: '#333333',
              fontSize: '16px',
              opacity: '0.8'
            },
            // Add these styles to remove autofill color
            "& input:-webkit-autofill": {
              WebkitBoxShadow: "0 0 0 100px #ffffff inset",
              WebkitTextFillColor: "#333333",
            },
            "& input:-webkit-autofill:focus": {
              WebkitBoxShadow: "0 0 0 100px #ffffff inset",
              WebkitTextFillColor: "#333333",
            }
          }}
          slotProps={{
            input: {
              endAdornment: <InputAdornment position="start"><IconButton onClick={handleClickShowConfirmPassword}
                onMouseDown={() => handleMouseDownPassword}
                onMouseUp={() => handleMouseDownPassword}  >
                {showConfirmPassword ? <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M15.5799 11.9999C15.5799 13.9799 13.9799 15.5799 11.9999 15.5799C10.0199 15.5799 8.41992 13.9799 8.41992 11.9999C8.41992 10.0199 10.0199 8.41992 11.9999 8.41992C13.9799 8.41992 15.5799 10.0199 15.5799 11.9999Z" stroke="#333333" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  <path d="M12.0001 20.27C15.5301 20.27 18.8201 18.19 21.1101 14.59C22.0101 13.18 22.0101 10.81 21.1101 9.39997C18.8201 5.79997 15.5301 3.71997 12.0001 3.71997C8.47009 3.71997 5.18009 5.79997 2.89009 9.39997C1.99009 10.81 1.99009 13.18 2.89009 14.59C5.18009 18.19 8.47009 20.27 12.0001 20.27Z" stroke="#333333" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                  : <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M14.5299 9.47004L9.46992 14.53C8.81992 13.88 8.41992 12.99 8.41992 12C8.41992 10.02 10.0199 8.42004 11.9999 8.42004C12.9899 8.42004 13.8799 8.82004 14.5299 9.47004Z" stroke="#BABABA" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M17.8201 5.76998C16.0701 4.44998 14.0701 3.72998 12.0001 3.72998C8.47009 3.72998 5.18009 5.80998 2.89009 9.40998C1.99009 10.82 1.99009 13.19 2.89009 14.6C3.68009 15.84 4.60009 16.91 5.60009 17.77" stroke="#BABABA" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M8.41992 19.5301C9.55992 20.0101 10.7699 20.2701 11.9999 20.2701C15.5299 20.2701 18.8199 18.1901 21.1099 14.5901C22.0099 13.1801 22.0099 10.8101 21.1099 9.40005C20.7799 8.88005 20.4199 8.39005 20.0499 7.93005" stroke="#BABABA" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M15.5099 12.7C15.2499 14.11 14.0999 15.26 12.6899 15.52" stroke="#BABABA" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M9.47 14.53L2 22" stroke="#BABABA" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M22 2L14.53 9.47" stroke="#BABABA" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                }
              </IconButton>
              </InputAdornment>,
              startAdornment: <InputAdornment position="start"><Box display={"flex"}
                onMouseDown={() => handleMouseDownPassword}
                onMouseUp={() => handleMouseDownPassword}  >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M6 10V8C6 4.69 7 2 12 2C17 2 18 4.69 18 8V10" stroke="#333333" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  <path d="M12 18.5C13.3807 18.5 14.5 17.3807 14.5 16C14.5 14.6193 13.3807 13.5 12 13.5C10.6193 13.5 9.5 14.6193 9.5 16C9.5 17.3807 10.6193 18.5 12 18.5Z" stroke="#333333" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  <path d="M17 22H7C3 22 2 21 2 17V15C2 11 3 10 7 10H17C21 10 22 11 22 15V17C22 21 21 22 17 22Z" stroke="#333333" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </Box>
              </InputAdornment>
            },
          }}
        />
      </Grid>
    </Grid>
  );
};

const Step2 = ({ formData, setFormData, setErrors }: { 
  formData: UserFormData, 
  setFormData: (data: UserFormData) => void,
  setErrors: React.Dispatch<React.SetStateAction<Record<string, string>>>
}) => {
  const handleRoleChange = (product: 'easywebinar' | 'easycrm') => (event: any) => {
    const value = event.target.value as Roles;
    setFormData({
      ...formData,
      [product === 'easywebinar' ? 'easywebinarRole' : 'easycrmeRole']: value
    });
    
    // Clear submit error if at least one role is selected
    const otherRole = product === 'easywebinar' ? formData.easycrmeRole : formData.easywebinarRole;
    // if (value !== Roles.NONE || otherRole !== Roles.NONE) {
    //   setErrors(prev => ({ ...prev, submit: '' }));
    // }
  };

  return (
    <Box>
      <Typography color="#333" fontSize="24px" fontWeight="500">
        Set Role
      </Typography>
      <Typography fontSize="14px" color="#666" marginBottom="24px">
        The admin role gives that person permission to manage other users and allows them to view billing information.
        You can change assigned roles later if needed.
      </Typography>

      <Box display="flex" alignItems="center" gap={2} marginBottom="10px">
        <Typography>User Role*</Typography>
        <Box display="flex">
          <img
            src="https://easywebinar-site.easywebinar.com/images/autheticator-app-images/easywebinar-logo.png"
            alt="easy-webinar-logo"
            width="21"
            height="21"
          />
        </Box>
      </Box>
      <Box>
        <FormControl sx={{ maxWidth: 292, width: "100%" }}>
          <Select
            value={formData.easywebinarRole}
            onChange={handleRoleChange('easywebinar')}
            input={<OutlinedInput />}
            displayEmpty
            renderValue={(selected) => (
              <Typography color="#333" fontSize="14px" fontWeight="500">
                {selected}
              </Typography>
            )}
            sx={{ height: "38px", maxWidth: "292px", width: "100%", "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
        borderColor: "#178EFC",
        borderWidth: "1px"
      }, }}
            MenuProps={{
              PaperProps: {
                sx: {
                  borderRadius: "12px",
                  mt: 1, // Add margin top for better spacing
                  "& .MuiMenu-list": {
                    padding: "8px",
                  },
                  "& .MuiMenuItem-root": {
                    padding: 0,
                    borderRadius: "12px",
                    "&:hover": {
                      backgroundColor: "transparent"
                    }
                  }
                }
              }
            }}
          >
            {Object.values(Roles).map((role) => (
              <MenuItem key={role} value={role} sx={{ py: "0", ":hover": { background: "unset" }, maxWidth: "292px", "&.MuiPaper-root": {
                borderRadius: "12px"
              } }}>
                <Box padding="10px" width="100%" sx={{
                  ":hover": {
                    backgroundColor: "#DFEBFF",
                    borderRadius: "12px",
                  }
                }}>
                  <Typography mb="3px" color="#333" fontSize="14px" fontWeight="500">
                    {role}
                  </Typography>
                  <Typography color="#333" fontSize="12px" whiteSpace="pre-wrap">
                    {role === Roles.ADMIN ? 'Full access to everything in the organization including all settings' :
                      'Basic access to organization features'}
                  </Typography>
                </Box>
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* CODE IS COMMENTED AS PER RECOMENDATION AS PER NAMAN SHARMA - ITS NOT LAUNCHED YET  */}

      {/* <Box display="flex" alignItems="center" gap={2} marginBottom="10px" marginTop="24px">
        <Typography>User Role*</Typography>
        <Box display="flex">
          <img src="https://easywebinar-site.easywebinar.com/images/autheticator-app-images/easycrm-logo.png" />
        </Box>
      </Box>
      <Box>
        <FormControl sx={{ maxWidth: 292 }}>
          <Select
            value={formData.easycrmeRole}
            onChange={handleRoleChange('easycrm')}
            input={<OutlinedInput />}
            displayEmpty
            sx={{ height: "38px" }}
          >
            {Object.values(Roles).map((role) => (
              <MenuItem key={role} value={role} sx={{ py: "0", ":hover": { background: "unset" } }}>
                <Box padding="10px" width="100%" sx={{
                  ":hover": {
                    backgroundColor: "#DFEBFF",
                    borderRadius: "8px",
                  }
                }}>
                  <Typography mb="3px" color="#333" fontSize="14px" fontWeight="500">
                    {role}
                  </Typography>
                  <Typography color="#333" fontSize="12px">
                    {role === Roles.ADMIN ? 'Full access to everything in the organization including all settings' :
                      role === Roles.MANAGER ? 'Manage team members and view organization settings' :
                      role === Roles.NONE ? 'No user role will be assigned to this user' :
                      'Basic access to organization features'}
                  </Typography>
                </Box>
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box> */}

      {/* CODE IS COMMENTED AS PER RECOMENDATION AS PER NAMAN SHARMA - ITS NOT LAUNCHED YET  */}

    </Box>
  );
};

const Step3 = () => (
  <Box display="flex" justifyContent="center" flexDirection="column" alignItems="center" py={{ xs: "30px", sm: "70px" }}>
    <Box mb="12px">
      <img src="https://easywebinar-site.easywebinar.com/images/autheticator-app-images/badge-check-solid.svg" alt="badge-checker" />
    </Box>
    <Typography fontSize="24px" color="#333" fontWeight="500">Account successfully created</Typography>
  </Box>
);

interface StepContentProps {
  step: number;
  formData: UserFormData;
  setFormData: (data: UserFormData) => void;
  errors: Record<string, string>;
  setErrors: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  profilePhoto: File | null;
  profilePhotoUrl: string;
  handleFileSelect: (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleUploadClick: () => void;
  fileInputRef: React.RefObject<HTMLInputElement>;
  showPassword: boolean;
  showConfirmPassword: boolean;
  handleClickShowPassword: () => void;
  handleClickShowConfirmPassword: () => void;
  handleMouseDownPassword: (event: React.MouseEvent<HTMLButtonElement>) => void;
}

const StepContent: React.FC<StepContentProps> = ({ 
  step, 
  formData, 
  setFormData, 
  errors, 
  setErrors,
  profilePhoto,
  profilePhotoUrl,
  handleFileSelect,
  handleUploadClick,
  fileInputRef,
  showPassword,
  showConfirmPassword,
  handleClickShowPassword,
  handleClickShowConfirmPassword,
  handleMouseDownPassword
}) => {
  const components = [
    <Step1 
      formData={formData} 
      setFormData={setFormData} 
      errors={errors} 
      setErrors={setErrors}
      profilePhoto={profilePhoto}
      profilePhotoUrl={profilePhotoUrl}
      handleFileSelect={handleFileSelect}
      handleUploadClick={handleUploadClick}
      fileInputRef={fileInputRef}
      showPassword={showPassword}
      showConfirmPassword={showConfirmPassword}
      handleClickShowPassword={handleClickShowPassword}
      handleClickShowConfirmPassword={handleClickShowConfirmPassword}
      handleMouseDownPassword={handleMouseDownPassword}
    />, 
    <Step2 formData={formData} setFormData={setFormData} setErrors={setErrors}/>, 
    <Step3 />
  ];
  return components[step];
};

const AddUser: React.FC<AddUserProps> = ({ open, onClose, onConfirm }) => {
  const [activeStep, setActiveStep] = React.useState(0);
  const [isLoading, setIsLoading] = React.useState(false);
  const [showPassword, setShowPassword] = React.useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = React.useState(false);
  const [isAuthorized, setIsAuthorized] = React.useState(false);
  
  // Add state for profile photo
  const [profilePhoto, setProfilePhoto] = React.useState<File | null>(null);
  const [profilePhotoUrl, setProfilePhotoUrl] = React.useState<string>('');
  const [uploadingPhoto, setUploadingPhoto] = React.useState(false);
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  
  const getTimezoneString = () => {
    const timezone = moment.tz.guess() || 'UTC';
    const offset = moment.tz(timezone).format('Z');
    const zoneName = moment.tz(timezone).zoneName();
    return `(GMT${offset}),${timezone},${zoneName}`;
  };

  const initialFormData: UserFormData = {
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    password: '',
    confirmPassword: '',
    timezone: getTimezoneString(),
    easywebinarRole: Roles.ADMIN,
    easycrmeRole: Roles.ADMIN
  };

  const [formData, setFormData] = React.useState<UserFormData>(initialFormData);
  const [errors, setErrors] = React.useState<Record<string, string>>({});

  // Security: Validate user permissions when modal opens
  React.useEffect(() => {
    if (open) {
      try {
        const permissionCheck = validateUserPermissions('OWNER');
        if (!permissionCheck.isValid) {
          toast.error(permissionCheck.error || 'Access denied: Insufficient permissions');
          onClose();
          return;
        }
        setIsAuthorized(true);
      } catch (error) {
        console.error('Error validating permissions:', error);
        toast.error('Error validating permissions');
        onClose();
      }
    } else {
      setIsAuthorized(false);
    }
  }, [open, onClose]);

  const handleClickShowPassword = () => setShowPassword((show) => !show);
  const handleClickShowConfirmPassword = () => setShowConfirmPassword((show) => !show);

  const handleMouseDownPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
  };

  // Handle file selection with enhanced security validation
  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      const file = event.target.files[0];
      
      // Security: Validate file type with whitelist approach
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
      if (!allowedTypes.includes(file.type)) {
        toast.error('Please select a valid image file (JPEG, PNG, GIF, or WebP)');
        return;
      }
      
      // Security: Check file size (limit to 5MB)
      const maxSize = 5 * 1024 * 1024; // 5MB
      if (file.size > maxSize) {
        toast.error('File size too large. Please select an image under 5MB');
        return;
      }
      
      // Security: Check for minimum file size to prevent empty files
      if (file.size < 100) { // 100 bytes minimum
        toast.error('File appears to be corrupted or empty');
        return;
      }
      
      // Security: Validate file name for potential path traversal
      const fileName = file.name;
      if (fileName.includes('..') || fileName.includes('/') || fileName.includes('\\')) {
        toast.error('Invalid file name');
        return;
      }
      
      setProfilePhoto(file);
      
      // Create a preview URL
      const objectUrl = URL.createObjectURL(file);
      setProfilePhotoUrl(objectUrl);
    }
  };
  
  // Trigger file input click
  const handleUploadClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };
  
  // Upload profile photo
  const uploadProfilePhoto = async (): Promise<string | null> => {
    if (!profilePhoto) return null;
    
    try {
      setUploadingPhoto(true);
      const formData = new FormData();
      formData.append('file', profilePhoto);
      
      const response = await AuthService.uploadProfilePhoto(formData);
      return response.data.url;
    } catch (error: any) {
      toast.error(error.message || 'Failed to upload profile photo');
      return null;
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleClose = () => {
    if (activeStep === steps.length - 1) {
      onConfirm();
    }
    setActiveStep(0);
    setFormData(initialFormData);
    setErrors({});
    
    // Clear profile photo state
    setProfilePhoto(null);
    if (profilePhotoUrl) {
      URL.revokeObjectURL(profilePhotoUrl);
      setProfilePhotoUrl('');
    }
    
    onClose();
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    
    // Security: Validate user permissions before allowing form submission
    const permissionCheck = validateUserPermissions('OWNER');
    if (!permissionCheck.isValid) {
      newErrors.submit = permissionCheck.error || 'Access denied: Insufficient permissions';
      setErrors(newErrors);
      return false;
    }
    
    // Check for required fields with hyperlink injection protection
    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    } else {
      const firstNameValidation = validateAndSanitizeName(formData.firstName, 'First name');
      if (!firstNameValidation.isValid) {
        newErrors.firstName = firstNameValidation.error || 'Invalid first name';
      }
    }
    
    if (!formData.email) newErrors.email = 'Email is required';
    if (!formData.phoneNumber) newErrors.phoneNumber = 'Phone number is required';
    if (!formData.password) newErrors.password = 'Password is required';
    if (!formData.confirmPassword) newErrors.confirmPassword = 'Please confirm your password';

    // Enhanced email validation with security checks
    if (formData.email) {
      const emailValidation = validateEmail(formData.email);
      if (!emailValidation.isValid) {
        newErrors.email = emailValidation.error || 'Please enter a valid email address';
      }
    }

    // Validate phone number with enhanced security
    const phoneDigits = formData.phoneNumber.replace(/\D/g, '');
    if (formData.phoneNumber && (phoneDigits.length < 10 || phoneDigits.length > 15)) {
      newErrors.phoneNumber = 'Please enter a valid phone number';
    }

    // Enhanced password validation
    if (formData.password && (
      formData.password.length < 8 ||
      !/\d/.test(formData.password) ||
      !/[!@#$%^&*(),.?":{}|<>]/.test(formData.password)
    )) {
      newErrors.password = 'Password must be at least 8 characters long and contain at least one number and one special character';
    }

    // Check if passwords match
    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    // Security: Validate first name for potential injection attacks
    if (formData.firstName && /[<>'"&;|$`]/.test(formData.firstName)) {
      newErrors.firstName = 'First name contains invalid characters';
    }

    // Security: Validate last name for potential injection attacks
    if (formData.lastName && /[<>'"&;|$`]/.test(formData.lastName)) {
      newErrors.lastName = 'Last name contains invalid characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = async () => {
    if (activeStep === 0) {
      const isValid = validateForm();
      if (!isValid) {
        return;
      }
    }

    if (activeStep === 1) {
      // if (formData.easywebinarRole === Roles.NONE && formData.easycrmeRole === Roles.NONE) {
      //   setErrors({ submit: 'At least one role must be assigned' });
      //   return;
      // }
    }
      
    // Set loading when moving from step 2 to 3
    if (activeStep === steps.length - 2) {
      setIsLoading(true);
      try {
        // Security: Validate user permissions before creating user
        const permissionCheck = validateUserPermissions('OWNER');
        if (!permissionCheck.isValid) {
          throw new Error(permissionCheck.error || 'Access denied: Insufficient permissions');
        }
        
        const applicationRoles = [];
        
        applicationRoles.push({ application: 'EASYWEBINAR', role: formData.easywebinarRole });
        applicationRoles.push({ application: 'EASYCRM', role: formData.easycrmeRole });

        // Security: Use validated company ID from permission check
        const companyId = permissionCheck.companyId;
        if (!companyId) {
          throw new Error('Invalid company access');
        }
        
        // Upload profile photo if selected
        let photoUrl = null;
        if (profilePhoto) {
          photoUrl = await uploadProfilePhoto();
        }

        // Security: Sanitize input data before sending to API
        // Security: Sanitize names before sending to prevent hyperlink injection
        const sanitizedFirstName = sanitizeTextInput(formData.firstName.trim());
        const sanitizedLastName = sanitizeTextInput(formData.lastName?.trim() || '');
        
        const signupRequest: SignupRequest = {
          email: formData.email.trim().toLowerCase(), // Normalize email
          password: formData.password,
          firstName: sanitizedFirstName, // Sanitized first name to prevent hyperlink injection
          lastName: sanitizedLastName, // Sanitized last name to prevent hyperlink injection
          phoneNumber: formData.phoneNumber.trim(), // Sanitize phone
          timezone: formData.timezone,
          companyId: companyId, // Use validated company ID
          applicationRoles,
          profileImage: photoUrl || undefined
        };
        
        await AuthService.signup(signupRequest);
        setActiveStep((prev) => prev + 1);
      } catch (error: any) {
        console.error('Error creating user:', error);
        setErrors({ submit: error.message || 'Failed to create user. Please try again.' });
      } finally {
        setIsLoading(false);
      }
    } else {
      setActiveStep((prev) => prev + 1);
    }
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleReset = () => {
    setActiveStep(0);
  };

  // Clean up object URLs when component unmounts
  React.useEffect(() => {
    return () => {
      if (profilePhotoUrl) {
        URL.revokeObjectURL(profilePhotoUrl);
      }
    };
  }, [profilePhotoUrl]);

  // Security: Don't render modal if user is not authorized
  if (!isAuthorized) {
    return null;
  }

  return (
    <Dialog 
      open={open} 
      onClose={handleClose} 
      className='card-details-modal' 
      sx={{ '& .MuiDialog-paper': { width: '752px', maxWidth: '752PX', borderRadius: '24px', border: '1px solid #E8E8EA' } }}
    >
      <DialogTitle sx={{ mb: "32px" }} p={{ xs: "16px 16px 15px !important" }} borderBottom="1px solid #E8E8F0">
        <Typography color='#333333' fontSize="min(24px, calc(20px + 4 * ((100vw - 320px) / (1920 - 320))))" fontWeight='500'>Add User</Typography>
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={() => ({
            position: 'absolute',
            right: { xs: "17px" },
            top: { xs: "15px" },
          })}
        >
          <ModalClose></ModalClose>
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ borderTop: 'none', borderBottom: 'none', p: { xs: "0 16px 16px", md: "0 32px 32px"} }}>
        <Box sx={{ width: '100%' }}>
          <Stepper activeStep={activeStep}>
            {steps.map((label, index) => (
              <Step key={index}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
          <Box sx={{ mt: 3 }}>
            <StepContent 
              step={activeStep} 
              formData={formData} 
              setFormData={setFormData} 
              errors={errors}
              setErrors={setErrors}
              profilePhoto={profilePhoto}
              profilePhotoUrl={profilePhotoUrl}
              handleFileSelect={handleFileSelect}
              handleUploadClick={handleUploadClick}
              fileInputRef={fileInputRef}
              showPassword={showPassword}
              showConfirmPassword={showConfirmPassword}
              handleClickShowPassword={handleClickShowPassword}
              handleClickShowConfirmPassword={handleClickShowConfirmPassword}
              handleMouseDownPassword={handleMouseDownPassword}
            />
          </Box>
          {errors.submit && (
            <Typography color="error" sx={{ mt: 2 }}>
              {errors.submit}
            </Typography>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ mb: { xs: "16px", sm: "16px", md: "32px" }, mr: { xs: "11px", sm: "16px", md: "32px" }, p: "0" }}>
        {activeStep !== steps.length - 1 && (
          <Button 
            onClick={handleBack} 
            color="primary" 
            disabled={activeStep === 0 || isLoading}
            sx={{
              border: '1px solid #E8EAF0', 
              px: '22px', 
              color: '#2B2B30 !important', 
              textTransform: 'capitalize', 
              borderRadius: '8px', 
              mr: '16px', 
              '&:hover': {
                border: '1px solid #E8EAF0 !important',
                boxShadow: 'unset !important'
              },
            }}
          >
            Back
          </Button>
        )}
        <Button 
          onClick={activeStep === steps.length - 1 ? handleClose : handleNext}
          disabled={isLoading}
          sx={{
            textTransform: 'capitalize', 
            bgcolor: '#178EFC', 
            color: '#FFFFFF !important', 
            px: '24px', 
            height: '40px', 
            borderRadius: '8px', 
            '&:hover': {
              boxShadow: 'none !important',
            },
          }}
        >
          {isLoading ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            activeStep === steps.length - 1 ? 'Done' : 'Next'
          )}
        </Button>
      </DialogActions>
      
      {/* Hidden file input for profile photo upload */}
      <input
        type="file"
        ref={fileInputRef}
        style={{ display: 'none' }}
        accept="image/*"
        onChange={handleFileSelect}
      />
    </Dialog>
  );
};
export default AddUser;



